import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type {
  QuickReportData,
  QuickReportGroup,
  ReportPeriod,
} from "../pages/Accounting/ChartOfAccounts/run-report/types";
import {
  MOCK_QUICK_REPORT_ROWS,
  MOCK_ACCOUNT_NAME_BY_ID,
} from "../pages/Accounting/ChartOfAccounts/run-report/mockData";
import {
  periodToDateRange,
  computeRunningBalance,
  groupByDistributionAccount,
  filterByDateRange,
} from "../pages/Accounting/ChartOfAccounts/run-report/utils";
import { DEFAULT_PERIOD } from "../pages/Accounting/ChartOfAccounts/run-report/constants";

interface Opts {
  accountId: string;
  period: ReportPeriod;
  customFrom?: string;
  customTo?: string;
}

interface HookResult {
  data: QuickReportData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useRunReport({ accountId, period, customFrom, customTo }: Opts): HookResult {
  const [searchParams] = useSearchParams();
  const forceEmpty = searchParams.get("empty") === "1";
  const forceLoading = searchParams.get("loading") === "1";
  const forceError = searchParams.get("error") === "1";
  const forcePopulated = searchParams.get("populated") === "1";

  const [tick, setTick] = useState(0);

  const data = useMemo<QuickReportData | null>(() => {
    if (forceLoading || forceError) return null;

    const effectivePeriod = period ?? DEFAULT_PERIOD;
    const { from, to } =
      effectivePeriod === "custom" && customFrom && customTo
        ? { from: customFrom, to: customTo }
        : periodToDateRange(effectivePeriod);

    const accountName =
      MOCK_ACCOUNT_NAME_BY_ID[accountId] ?? MOCK_ACCOUNT_NAME_BY_ID.default;

    if (forceEmpty) {
      return {
        accountId,
        accountName,
        period: effectivePeriod,
        from,
        to,
        groups: [],
        total: 0,
        endBalance: 0,
      };
    }

    const inRange = forcePopulated
      ? MOCK_QUICK_REPORT_ROWS
      : filterByDateRange(MOCK_QUICK_REPORT_ROWS, from, to);

    if (inRange.length === 0) {
      return {
        accountId,
        accountName,
        period: effectivePeriod,
        from,
        to,
        groups: [],
        total: 0,
        endBalance: 0,
      };
    }

    const withBalance = computeRunningBalance(inRange);
    const groups: QuickReportGroup[] = groupByDistributionAccount(withBalance);
    const total = withBalance.reduce((s, r) => s + r.amount, 0);
    const endBalance = withBalance[withBalance.length - 1]?.balance ?? 0;

    return {
      accountId,
      accountName,
      period: effectivePeriod,
      from,
      to,
      groups,
      total,
      endBalance,
    };
  }, [accountId, period, customFrom, customTo, forceEmpty, forcePopulated, forceLoading, forceError, tick]);

  return {
    data,
    loading: forceLoading,
    error: forceError ? "Something went wrong loading this report. Please try again." : null,
    refetch: () => setTick((t) => t + 1),
  };
}